import memberWorkspaceOverviewStyles from './member-workspace-overview.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import type {
  LoginGroup,
  LoginGroupImis,
  LoginDomainScopeGrant,
  MembershipStatus,
} from '../../../api/agwa-api-model';
import {
  MEMBERSHIP_LABELS,
  MEMBERSHIP_ORDER,
  toMembershipStatus,
} from '../shared/company-status-display';
import '../../../components/state-badge/agwa-state-badge';
import { showErrorToast } from '../../../utility/notifications/easy-toast';
import type { MemberWorkspaceStore } from './member-workspace-store';
// Hidden, not deleted (architect, 2026-09-05): company-level operation-type management is
// retired while types are site-scoped; the panel stays importable for reintroduction.
// import './member-domain-scopes-panel';
import './company-operation-types-section';

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/skeleton/skeleton.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { terms } from '../../../language/terms';

@customElement('member-workspace-overview')
export class MemberWorkspaceOverview extends LitElement {
  static styles = [sharedStyles, unsafeCSS(memberWorkspaceOverviewStyles)];

  @property({ type: Boolean }) loading = false;
  @property({ attribute: false }) group: LoginGroup | null = null;
  @property({ attribute: false }) imis: LoginGroupImis | null = null;
  @property({ attribute: false }) domains: LoginDomainScopeGrant[] = [];
  @property({ attribute: false }) store: MemberWorkspaceStore | null = null;

  @state() private editing = false;
  @state() private saving = false;
  @state() private editData: Partial<LoginGroup> = {};

  /**
   * The workspace ACTION BAR drives editing since 2026-09-05 ("the edit and save actions
   * are moved there instead of being in the body" - one action grammar across product,
   * company and site). These three are the bar's public API; editing-changed keeps the
   * bar's own buttons in step with the state that lives here.
   */
  private emitEditState() {
    this.dispatchEvent(
      new CustomEvent('editing-changed', {
        detail: { editing: this.editing, saving: this.saving },
        bubbles: true,
        composed: true,
      })
    );
  }

  startEdit() {
    if (!this.group) return;
    this.editData = {
      name: this.group.name,
      userNamePrefix: this.group.userNamePrefix,
      active: this.group.active,
      // Normalised on the way in, or the select opens with nothing selected: the API sends
      // "member" while the options are valued "Member". See toMembershipStatus for why the two
      // disagree. A select that silently shows no current value is how an unrelated edit comes to
      // clear a field nobody touched.
      membershipStatus: toMembershipStatus(this.group.membershipStatus),
    };
    this.editing = true;
    this.emitEditState();
  }

  cancelEdit() {
    this.editing = false;
    this.editData = {};
    this.emitEditState();
  }

  /**
   * Saves the edit and answers whether it saved, so the host releases the company's edit lock
   * only after a save that landed (#398 C11, @LOCK-034); a failed save keeps the form and the lock.
   */
  async saveEdit(): Promise<boolean> {
    if (!this.store) return false;
    this.saving = true;
    this.emitEditState();
    const saved = await this.store.saveGroup(this.editData);
    this.saving = false;
    if (saved.ok) {
      this.editing = false;
      this.editData = {};
    } else if (!saved.unanswered) {
      // The server's reason, so the person knows what to fix (#406, @LOCK-065), as the server sent
      // it so a person it names shows by their current name (@VOC-SERVER-002). Nothing for a
      // request that got no answer: the connection monitor tells the person (D-144).
      showErrorToast('Save Failed', saved.errorKept ?? saved.error);
    }
    this.emitEditState();
    return saved.ok;
  }

  private handleFieldChange(field: keyof LoginGroup, value: unknown) {
    this.editData = { ...this.editData, [field]: value };
  }

  private renderDetailsSection() {
    if (this.loading && !this.group) {
      return this.renderDetailsSkeleton();
    }

    if (this.editing) {
      return this.renderDetailsEdit();
    }

    return this.renderDetailsReadonly();
  }

  private renderDetailsSkeleton() {
    return html`
      <sl-card>
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="people"></sl-icon>
            ${terms.loginGroup.value} Details
          </span>
        </div>
        <div class="info-grid">
          ${[1, 2, 3].map(
            () => html`
              <div class="info-item">
                <sl-skeleton
                  effect="pulse"
                  style="width: 60%; height: 0.75rem;"
                ></sl-skeleton>
                <sl-skeleton
                  effect="pulse"
                  style="width: 80%; height: 1rem; margin-top: 0.25rem;"
                ></sl-skeleton>
              </div>
            `
          )}
        </div>
      </sl-card>
    `;
  }

  private renderDetailsReadonly() {
    const group = this.group;
    if (!group) return nothing;

    // LEAN ON PURPOSE (architect, 2026-09-05: "the name is shown twice... the status badge is
    // in the header and in the details section... not inline with other pages like product").
    // The BANNER above this card owns the name, the username prefix and the operating-status
    // chip; this card says only what the banner does not: membership and the iMIS link. A
    // company has no description (D-138). Name and prefix return in EDIT mode, where they are
    // the work itself.
    //
    // Block/Unblock sits in the card header beside Edit rather than moving to the banner,
    // and that placement is load-bearing: chain-support's setCompanyBlocked and the
    // @CO-MEMBER-004 journey both read the company's blocked state FROM THIS BUTTON, scoped
    // inside <member-workspace-overview> - the button is the one unambiguous carrier of the
    // flag now that the chip lives in the banner (see setCompanyBlocked for why a chip
    // locator here once unblocked the wrong thing).
    return html`
      <sl-card>
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="people"></sl-icon>
            ${terms.loginGroup.value} Details
          </span>
          <!-- Every action - Block, Edit, and Save/Cancel while editing - lives on the
               workspace ACTION BAR (architect, 2026-09-05: consistency across product,
               company and site). The card body carries only the record. -->
        </div>

        <!--
          LABEL:VALUE ROWS, the product detail page's layout (architect, 2026-09-05: "the
          tabbing and label value layout... feels like a cleaner design") - classes mirror
          product-registry-shared.css's detail-rows so the two surfaces read as one design.
          Membership deliberately does NOT sit beside an operating-status chip - the status
          lives once, in the banner; banner=operational state, card=the record.
        -->
        <div class="detail-rows">
          <div class="detail-row">
            <span class="detail-label">${terms.membership.value}</span>
            <span class="detail-value">
              <agwa-state-badge
                domain="membership"
                state=${group.membershipStatus}
              ></agwa-state-badge>
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">${terms.imis.value} Reference</span>
            <span
              class="detail-value ${this.imis?.reference ? '' : 'placeholder'}"
              >${this.imis?.reference ?? 'Not linked'}</span
            >
          </div>
        </div>
      </sl-card>
    `;
  }

  private renderDetailsEdit() {
    return html`
      <sl-card>
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="people"></sl-icon>
            Edit ${terms.loginGroup.value} Details
          </span>
        </div>

        <div class="edit-form">
          <div class="edit-row">
            <sl-input
              label="Name"
              value=${this.editData.name ?? ''}
              @sl-input=${(e: Event) =>
                this.handleFieldChange(
                  'name',
                  (e.target as HTMLInputElement).value
                )}
              required
            ></sl-input>
            <div class="info-item">
              <span class="info-label">${terms.loginUser.value} Prefix</span>
              <span
                class="info-value ${!this.group?.userNamePrefix ? 'empty' : ''}"
                >${this.group?.userNamePrefix || 'Not set'}</span
              >
              <span
                style="font-size: 0.75rem; color: var(--sl-color-neutral-500); margin-top: 0.25rem;"
                >Cannot be changed after creation</span
              >
            </div>
          </div>

          <!--
            No empty option, unlike the Add dialog. There the blank is the point - the question must
            be answered rather than defaulted. Here the company already has an answer, so offering a
            blank would invite clearing a fact to no purpose; the three real states are the whole
            choice.
          -->
          <!-- A RADIO GROUP, extending the create page's D-089 ruling to editing (architect,
               2026-09-05: "the member should also be a radio group when editing"): three
               mutually exclusive answers sit in the open, one click each, no hoisted popup.
               Options come from the same map the list and legend read. -->
          <sl-radio-group
            label="${terms.membership.value}"
            class="membership-choice"
            value=${this.editData.membershipStatus ?? ''}
            @sl-change=${(e: Event) =>
              this.handleFieldChange(
                'membershipStatus',
                (e.target as HTMLInputElement).value as MembershipStatus
              )}
            help-text="Whether this ${terms.loginGroup.lower} holds ${terms.agwa.value} ${terms.membership.lower}. It does not affect whether they can use the system."
          >
            ${MEMBERSHIP_ORDER.map(
              (status) =>
                html`<sl-radio value=${status}
                  >${MEMBERSHIP_LABELS[status]}</sl-radio
                >`
            )}
          </sl-radio-group>

          <!--
            The Active switch that used to sit here has been removed, and its removal is part of
            D-036 stage 2 rather than tidying.

            There were two ways to change this flag: this switch, saved silently with the rest of the
            form, and the confirmed Block / Unblock button on the read view. While the flag controlled
            nothing that was merely redundant. Now it stops an entire company working, including
            people mid-session, and an unconfirmed switch buried in a details form is not an
            acceptable way to do that - someone correcting a name could block a company by
            brushing a control they were not looking at.

            One path, and it asks first, through its own command (#406). A details save cannot
            change the flag at all: the store sends it as stored and the server ignores it
            (@LOCK-069).
          -->
          <!-- Save / Cancel live on the workspace action bar (2026-09-05). -->
        </div>
      </sl-card>
    `;
  }

  render() {
    if (!this.group && !this.loading) {
      return html`<sl-alert variant="neutral" open
        >No ${terms.loginGroup.lower} selected.</sl-alert
      >`;
    }

    return html`<div class="overview-grid">
      ${this.renderDetailsSection()} ${this.renderDomainManagement()}
    </div>`;
  }

  /**
   * ONE operation-types block on this page, and it is a READONLY reflection of the sites
   * (architect, 2026-09-05: the overview showed operation types twice, and company-level
   * registration is retired now that types are site-scoped). The management panel this
   * replaced - member-domain-scopes-panel - is HIDDEN, not deleted, in case company-level
   * types return; the Operation Types tab in member-workspace is hidden the same way.
   */
  private renderDomainManagement() {
    if (!this.group) return nothing;
    return html`<sl-card>
      <div class="section-header">
        <span class="section-title">
          <sl-icon name="diagram-3"></sl-icon>
          ${terms.userGroupDomain.plural}
        </span>
      </div>
      <company-operation-types-section
        .groupId=${this.group.id}
      ></company-operation-types-section>
    </sl-card>`;
  }
}
